import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'

const API_BASE = import.meta.env.VITE_API_URL || ''

// A transient failure (rate-limit or a flaky mobile connection) must not drop a
// photo. Retry the same photo a few times with a growing backoff before giving up.
const MAX_UPLOAD_ATTEMPTS = 3
const RETRY_BASE_MS = 800

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function MobilePhotoUpload() {
  const { token } = useParams()
  const [session, setSession] = useState(null)
  const [error, setError] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadedCount, setUploadedCount] = useState(0)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [successMessage, setSuccessMessage] = useState(null)

  useEffect(() => {
    validateSession()
  }, [token])

  async function validateSession() {
    try {
      const res = await fetch(`${API_BASE}/api/warehouse/photography/qr-session/${token}`)
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Invalid or expired session')
        return
      }
      setSession(data)
      setUploadedCount(data.uploadCount || 0)
    } catch {
      setError('Unable to connect. Please try again.')
    }
  }

  // Upload one photo, retrying transient failures (rate-limit / dropped
  // connection) with backoff. Returns { ok } on success, { terminal } when the
  // whole session is finished (limit reached / expired), or { error } for a
  // per-file failure that must not abandon the rest of the batch.
  async function uploadPhoto(file) {
    const formData = new FormData()
    formData.append('photo', file)
    let lastError = 'Upload failed'

    for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt++) {
      try {
        const res = await fetch(`${API_BASE}/api/warehouse/photography/qr-upload/${token}`, {
          method: 'POST',
          body: formData
        })
        const data = await res.json().catch(() => ({}))
        if (res.ok) return { ok: true, data }

        const message = data.error || 'Upload failed'
        // Session-level states (401 expired, 409 session full): terminal by status,
        // never by message text. Retrying or continuing the batch is pointless.
        if (res.status === 401 || res.status === 409) {
          return { terminal: true, error: message }
        }
        // Rate-limit is transient — back off and try the same photo again.
        if (res.status === 429) {
          lastError = message
          if (attempt < MAX_UPLOAD_ATTEMPTS) await sleep(RETRY_BASE_MS * attempt)
          continue
        }
        // Any other per-file error (e.g. a rejected file) will not improve on retry.
        return { error: message }
      } catch {
        lastError = 'Upload failed. Check your connection.'
        if (attempt < MAX_UPLOAD_ATTEMPTS) await sleep(RETRY_BASE_MS * attempt)
      }
    }
    return { error: lastError }
  }

  async function handleFileChange(e) {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    setUploading(true)
    setError(null)
    setSuccessMessage(null)

    const maxUploads = session?.maxUploads || 20
    let uploaded = 0
    let count = uploadedCount
    let terminalError = null
    const failed = []

    for (let i = 0; i < files.length; i++) {
      if (count >= maxUploads) {
        terminalError = `Upload limit reached (${maxUploads} photos). Scan a new QR code for more.`
        break
      }

      setUploadProgress(Math.round((i / files.length) * 100))
      const result = await uploadPhoto(files[i])

      if (result.ok) {
        uploaded++
        count = result.data.uploadCount
        setUploadedCount(count)
      } else if (result.terminal) {
        terminalError = result.error
        break
      } else {
        // Keep going — one bad photo must not strand the rest of the selection.
        failed.push(files[i].name)
      }
    }

    setUploadProgress(100)
    setUploading(false)

    if (uploaded > 0) {
      setSuccessMessage(`${uploaded} photo${uploaded !== 1 ? 's' : ''} uploaded successfully`)
    }
    // A session-level reason (full / expired) wins over the per-file list: it
    // explains why the batch stopped and must not be overwritten.
    if (terminalError) {
      setError(terminalError)
    } else if (failed.length > 0) {
      setError(`${failed.length} photo${failed.length !== 1 ? 's' : ''} couldn't upload: ${failed.join(', ')}. Please try those again.`)
    }
    // Reset file input
    e.target.value = ''
  }

  const remaining = session ? session.maxUploads - uploadedCount : 0

  if (error && !session) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.icon}>⚠️</div>
          <h1 style={styles.title}>Upload Unavailable</h1>
          <p style={styles.errorText}>{error}</p>
          <p style={styles.hint}>Please scan a new QR code from the photography page.</p>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.icon}>📸</div>
        <h1 style={styles.title}>Upload Photos</h1>
        <p style={styles.subtitle}>AudioTrader — Warehouse Photography</p>

        {session && (
          <div style={styles.stats}>
            <span>{uploadedCount} uploaded</span>
            <span style={styles.divider}>•</span>
            <span>{remaining} remaining</span>
          </div>
        )}

        {error && <div style={styles.errorBanner}>{error}</div>}
        {successMessage && <div style={styles.successBanner}>{successMessage}</div>}

        {uploading ? (
          <div style={styles.progressContainer}>
            <div style={styles.progressBar}>
              <div style={{ ...styles.progressFill, width: `${uploadProgress}%` }} />
            </div>
            <p style={styles.progressText}>Uploading... {uploadProgress}%</p>
          </div>
        ) : remaining > 0 ? (
          <div style={styles.buttonGroup}>
            <label style={styles.uploadButton}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                capture="environment"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              Take Photo
            </label>
            <label style={{ ...styles.uploadButton, background: '#6b7280' }}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              Choose from Gallery
            </label>
          </div>
        ) : (
          <p style={styles.hint}>Upload limit reached. Scan a new QR code if you need to upload more.</p>
        )}

        <p style={styles.hint}>JPG, PNG, or WebP • Max 20MB per photo</p>
      </div>
    </div>
  )
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#111827',
    padding: 20,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  card: {
    background: '#1f2937',
    borderRadius: 16,
    padding: '32px 24px',
    textAlign: 'center',
    maxWidth: 400,
    width: '100%',
    color: '#f3f4f6'
  },
  icon: { fontSize: 48, marginBottom: 12 },
  title: { margin: '0 0 4px', fontSize: 22, fontWeight: 700 },
  subtitle: { margin: '0 0 20px', fontSize: 13, color: '#9ca3af' },
  stats: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    fontSize: 14,
    color: '#d1d5db',
    marginBottom: 20,
    padding: '8px 16px',
    background: '#374151',
    borderRadius: 8
  },
  divider: { color: '#6b7280' },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    alignItems: 'center'
  },
  uploadButton: {
    display: 'inline-block',
    padding: '14px 32px',
    background: '#3b82f6',
    color: 'white',
    borderRadius: 10,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 12
  },
  progressContainer: { marginBottom: 12 },
  progressBar: {
    height: 8,
    background: '#374151',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 8
  },
  progressFill: {
    height: '100%',
    background: '#3b82f6',
    borderRadius: 4,
    transition: 'width 0.3s'
  },
  progressText: { fontSize: 14, color: '#9ca3af' },
  hint: { fontSize: 12, color: '#6b7280', margin: '8px 0 0' },
  errorText: { fontSize: 15, color: '#fbbf24', margin: '8px 0 16px' },
  errorBanner: {
    background: '#7f1d1d',
    color: '#fca5a5',
    padding: '8px 12px',
    borderRadius: 8,
    fontSize: 13,
    marginBottom: 12
  },
  successBanner: {
    background: '#064e3b',
    color: '#6ee7b7',
    padding: '8px 12px',
    borderRadius: 8,
    fontSize: 13,
    marginBottom: 12
  }
}

export default MobilePhotoUpload
